import { useTranslation } from 'react-i18next';

import type { IdPostCampaignDto, PostCampaignDto, StageChangeStatusDto } from '@/services/ws-post/src/main/resources/rest-api';
import { getStageChangeResult, getStageChangeStatus, StageChangeStatus } from '@/services/ws-post/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';

import { useUnaddressedMailingPositionErrorsNotifier } from './useUnaddressedMailingPositionErrorsNotification.ts';
import { AsyncChangeResultUnavailableError, useWaitForAsyncChange } from './useWaitForAsyncChange.ts';

type WaitForStageChange = (campaignId: IdPostCampaignDto) => Promise<PostCampaignDto | undefined>;

/**
 * Waits for the stage change that `PUT /post-campaigns/{id}` and `PATCH /post-campaigns/{campaignId}/accept` run in the
 * background when they answer 202: accepting or booking a campaign calls several Promopost APIs and can take longer
 * than the 100 s after which Cloudflare cuts the request.
 *
 * @returns A function resolving with the campaign the stage change produced, or `undefined` when it failed, gave up,
 * could not read the result of a finished change, or the component unmounted. Every outcome but an unmount is notified
 * about. A status poll that keeps failing rejects with its own error, the way every request failure does.
 */
export const useWaitForStageChange = (): WaitForStageChange => {
  const [t] = useTranslation(['backend', 'common']);
  const notify = useNotification();
  const notifyUnaddressedMailingPositionErrors = useUnaddressedMailingPositionErrorsNotifier();

  const waitForAsyncChange = useWaitForAsyncChange({
    statusApiMethod: getStageChangeStatus,
    resultApiMethod: getStageChangeResult,
    statusValues: StageChangeStatus,
    onError: ({ translationKey, unaddressedMailingPositionErrors }: StageChangeStatusDto) => {
      if (unaddressedMailingPositionErrors?.length) {
        notifyUnaddressedMailingPositionErrors(unaddressedMailingPositionErrors);
        return;
      }

      const body = translationKey ? t(`backend:${translationKey}`) : t('common:UNKNOWN_ERROR');
      notify.error({ title: t('common:ERROR'), body });
    },
  });

  return useCallbackRef<WaitForStageChange>(async (campaignId) => {
    try {
      return await waitForAsyncChange(campaignId);
    } catch (error) {
      // the aborts of a wait that was already notified about, which this hook reports as "no campaign"
      if (error == null || error instanceof AsyncChangeResultUnavailableError) {
        return undefined;
      }

      throw error;
    }
  });
};
